import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { sosAlarm } from './sosSound';

/**
 * Ties the alarm singleton (`sosSound.ts`) to React (slice 85, PLAN §8). Mounted
 * ONCE, in the SOS banner, which renders only while the shell is authenticated.
 *
 *   - While `shouldRing` is true, the alarm loops; when it clears (acknowledged,
 *     no SOS active, or every open SOS aged past the D5 freshness window) the alarm
 *     stops. Start/stop is idempotent. The caller decides what "should ring" means
 *     (SosBanner gates it on a FRESH open SOS, D5) — this hook only mirrors the flag.
 *   - A FIRST USER GESTURE anywhere in the app arms the AudioContext, so an SOS
 *     that fires later can sound without a dedicated tap. The listeners are
 *     `once`-style: removed as soon as the context is armed, so there is no
 *     steady-state cost. `capture: true` so a gesture arms even when a handler
 *     below stops propagation.
 *   - `enableSound` is the banner's one-tap control for the case where no gesture
 *     has armed audio yet AND an SOS is already active: it arms AND starts in the
 *     same gesture.
 *
 * `armed` is read through `useSyncExternalStore`, so the banner re-renders the
 * instant the context becomes usable and drops the "Enable sound" affordance.
 */
export function useSosAudio(shouldRing: boolean): { armed: boolean; enableSound: () => void } {
  const armed = useSyncExternalStore(
    (cb) => sosAlarm.subscribe(cb),
    () => sosAlarm.isArmed(),
    () => false,
  );

  // Start/stop the loop as the ring state changes.
  useEffect(() => {
    if (shouldRing) sosAlarm.start();
    else sosAlarm.stop();
  }, [shouldRing]);

  // Stop the alarm when the banner unmounts (sign-out), so it never outlives the
  // session.
  useEffect(() => () => sosAlarm.stop(), []);

  // Arm on the first gesture anywhere, then stop listening once armed.
  useEffect(() => {
    if (sosAlarm.isArmed()) return;
    const onGesture = () => {
      sosAlarm.arm();
      if (sosAlarm.isArmed()) detach();
    };
    const detach = () => {
      document.removeEventListener('pointerdown', onGesture, true);
      document.removeEventListener('keydown', onGesture, true);
    };
    document.addEventListener('pointerdown', onGesture, true);
    document.addEventListener('keydown', onGesture, true);
    return detach;
  }, []);

  const enableSound = useCallback(() => {
    sosAlarm.arm();
    sosAlarm.start();
  }, []);

  return { armed, enableSound };
}
